/**
 * Review requests (owner 10-21, item 5): an executive asks the admin one short question about a template;
 * the admin answers once. One set of text rules for the browser and the Worker. The text lives only in
 * the template_reviews table (never in the audit log) and never may hold an ID number.
 */
import { z } from 'zod';
import { idNumbersInText } from './id-validation';

export const REVIEW_TEXT_MAX = 400;
export const REVIEW_STATES = ['open', 'answered', 'done'] as const;
export type ReviewState = (typeof REVIEW_STATES)[number];

/** NFC, Windows line breaks as \n, trimmed, at most one empty line in a row. */
export function cleanReviewText(raw: string): string {
  return raw.normalize('NFC').replace(/\r\n?/g, '\n').trim().replace(/\n{3,}/g, '\n\n');
}

export type ReviewTextIssue = { code: 'empty' | 'too_long' | 'control' | 'id_number'; message: string };

/** The first problem with a message (fixed sentences, never echoing the text), or null. Counts characters, not bytes. */
export function reviewTextIssue(raw: string): ReviewTextIssue | null {
  const s = cleanReviewText(raw);
  if (!s) return { code: 'empty', message: 'Type a message first.' };
  if ([...s].length > REVIEW_TEXT_MAX) return { code: 'too_long', message: `At most ${REVIEW_TEXT_MAX} characters.` };
  // Any control character except a line break.
  if (/[^\P{Cc}\n]/u.test(s)) return { code: 'control', message: 'The message has a character that cannot be used.' };
  if (idNumbersInText(s).length) {
    return { code: 'id_number', message: 'Do not type Aadhaar, PAN or TAN numbers here. They stay on your computer only.' };
  }
  return null;
}

/** The browser form's schema: the cleaned text, or an issue with the same messages as the Worker. */
export const ReviewText = z.string().max(4000).transform(cleanReviewText).superRefine((s, ctx) => {
  const issue = reviewTextIssue(s);
  if (issue) ctx.addIssue({ code: 'custom', message: issue.message });
});
